
import { storeToRefs } from 'pinia';
import { CouponsWithImageUrl } from '@/lib/api/coupons.api';
import { apiClient } from '@/lib/api/client';
import { OdooLookupByPartnerRefResponse, OdooVehicles } from '@/lib/api/odoo.api';
import { useCouponStore } from '@/store/useCoupon';
import router from '@/router';
import { GenerateBatchIdData, useScannerCouponStore } from '@/store/useScannerCoupon';
import { useDebouncedRef } from '@/lib/hooks/useDebouncedRef';
import accounting from 'accounting';
import { onMounted, ref, defineComponent, computed, onUnmounted, watch } from 'vue';

export default defineComponent({
  setup() {
    const coupon = useCouponStore();
    const scannerCoupon = useScannerCouponStore();
    const oneCoupon = ref(null);

    const { error, openImport, enableClear, selectedCoupons, showReport } = storeToRefs(coupon);
    const {
      count,
      coupons,
      currentScanner,
      erroredCoupons,
      loading,
      listTotal,
      listPageSize,
      listTotalPages,
    } = storeToRefs(scannerCoupon);

    const routerId = ref(router.currentRoute.value.params.id as string);
    const searchQuery = ref('');
    const page = ref(1);
    const pageSizeOptions = [10, 25, 50];
    const pageSize = ref(25);
    const lastTypeFilter = ref<number[] | null>(null);
    const typeFilterReady = ref(false);

    const sortCoupons = (coupons: CouponsWithImageUrl[]) => {
      return coupons.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    };

    const unprocessedCoupons = computed(() => {
      if (batchId.value) {
        const filtered = coupons.value.filter((c) => {
          if (c.state === 'new') {
            return true;
          } else if (
            c.state === 'approved' &&
            (c.importState === 'pending-import' || c.importState === 'odoo-import-error')
          ) {
            return true;
          } else {
            return false;
          }
        });
        return sortCoupons(filtered);
      } else {
        return sortCoupons(coupons.value.concat(erroredCoupons.value));
      }
    });

    const allItems = computed(() => {
      if (currentScanner.value.isAppMembre) {
        // Server-paged for app-membre — do not hard-slice to 50.
        return unprocessedCoupons.value;
      }
      if (batchId.value) {
        // `coupons` is batch-scoped after `fetchBatchCoupons`; show every row in the batch
        // (including `odoo-import-success`) so success state matches app-membre. Keep
        // `unprocessedCoupons` elsewhere for counts, delete-all, and report gating.
        return sortCoupons(coupons.value).slice(0, 50);
      }
      return coupons.value.slice(0, 50);
    });

    const filteredItems = computed(() => {
      return sortCoupons(allItems.value);
    });

    const paginationRange = computed(() => {
      const size = listPageSize.value || pageSize.value;
      if (listTotal.value === 0) return '0 sur 0';
      const from = (page.value - 1) * size + 1;
      const to = Math.min(page.value * size, listTotal.value);
      return `${from}–${to} sur ${listTotal.value}`;
    });

    const onPageSizeChange = (value: unknown) => {
      const next = Number(value);
      if (!pageSizeOptions.includes(next) || next === pageSize.value) return;
      pageSize.value = next;
      page.value = 1;
    };

    const pageSizeSelectModel = computed(() => pageSize.value as unknown);

    const approvedTotal = computed(() => {
      const approvedCoupons = coupons.value.filter(
        (c) => c.state === 'approved' && c.importState === 'odoo-import-success',
      );

      if (!approvedCoupons.length) {
        return { total: 0, amount: '0,00' };
      }

      const rawAmount = approvedCoupons.reduce((total, coupon) => total + coupon.amount, 0);
      const formattedAmount = accounting.formatMoney(rawAmount, '', 2, ' ', ',');
      const total = approvedCoupons.length;

      return { total, amount: formattedAmount };
    });

    const rejectedTotal = computed(() => {
      const rejectedCoupons = coupons.value.filter((c) => c.state === 'rejected');
      if (!rejectedCoupons.length) {
        return { total: 0, amount: '0,00' };
      }

      const rawAmount = rejectedCoupons.reduce((total, coupon) => total + coupon.amount, 0);
      const formattedAmount = accounting.formatMoney(rawAmount, '', 2, ' ', ',');
      const total = rejectedCoupons.length;

      return { total, amount: formattedAmount };
    });

    watch(
      [approvedTotal, rejectedTotal],
      ([newApproved, newRejected], [oldApproved, oldRejected]) => {
        if (!oldApproved || !oldRejected) {
          return;
        }

        if (newApproved.total === oldApproved.total && newRejected.total === oldRejected.total) {
          return;
        }

        if (currentScanner.value.isAppMembre) {
          if (oneCoupon.value) {
            scrollTo();
          }

          return;
        }

        if (oneCoupon.value && batchId.value) {
          scrollTo();
        }
      },
      { immediate: true },
    );

    const unprocessedTotal = computed(() => {
      if (!unprocessedCoupons.value?.length) {
        return { total: 0, amount: '0,00' };
      }

      const rawAmount = unprocessedCoupons.value.reduce((sum, coupon) => sum + coupon.amount, 0);
      const formattedAmount = accounting.formatMoney(rawAmount, '', 2, ' ', ',');
      const total = unprocessedCoupons.value.length;

      return { total, amount: formattedAmount };
    });

    const driverLicence = ref('');
    const driverLicenceData = ref<OdooLookupByPartnerRefResponse | null>(null);
    const batchId = ref('');
    const vehicles = ref<OdooVehicles[]>([]);
    const vehicleId = ref(null);
    const driverId = ref<number | null>(null);
    const odooDriverId = ref<number | null>(null);
    const fleetVehicleId = ref<number | null>(null);
    const confirmOpen = ref(false);
    const deleteText = ref({ type: '', title: '', message: '' });
    const deleteData = ref({ id: 0 });

    const vehicle = computed(() => {
      try {
        return vehicles.value.find(v => v.id === vehicleId.value);
      } catch (error) {
        return null;
      }
    });

    const deleteCallback = async (type: string) => {
      const ids: number[] =
        type === 'one'
          ? [deleteData.value.id]
          : type === 'selected'
            ? selectedCoupons.value.map((c) => c.id)
            : unprocessedCoupons.value.map((c) => c.id);

      await coupon.deleteCoupons(ids);
      scannerCoupon.removeCouponsByIds(ids);

      // Keep `count` (API totals per scanner) in sync with deletes — same effect as master’s
      // Firestore listeners updating `count_new_status` / `count_error_status`.
      await scannerCoupon.refreshAllScannerCounts();

      if (currentScanner.value?.isAppMembre && typeFilterReady.value) {
        await fetchCoupons(lastTypeFilter.value);
      }

      if (allItems.value.length === 0) {
        clearScanner();
      }
    };

    function confirmDeleteAll(): void {
      confirmOpen.value = true;
      deleteText.value.title = 'Supprimer tous les coupons';
      deleteText.value.message = 'Êtes-vous sûr de vouloir supprimer tous les coupons?';
      deleteText.value.type = 'all';
    }

    function confirmDeleteSelected(): void {
      confirmOpen.value = true;
      deleteText.value.message = 'Êtes-vous sûr de vouloir supprimer les coupons sélectionnés?';
      deleteText.value.title = 'Supprimer les coupons sélectionnés';
      deleteText.value.type = 'selected';
    }

    function confirmDeleteOne(event: { id: number; importState: string }): void {
      confirmOpen.value = true;
      deleteData.value = event;
      deleteText.value.message = 'Êtes-vous sûr de vouloir supprimer ce coupon?';
      deleteText.value.title = 'Supprimer ce coupon';
      deleteText.value.type = 'one';
    }

    const onceLookUp = (data: OdooLookupByPartnerRefResponse) => {
      driverLicenceData.value = data;
      vehicles.value = driverLicenceData.value.vehicles || [];
      if (vehicles.value.length === 1) {
        vehicleId.value = vehicles.value[0].id;
      } else {
        vehicleId.value = null;
      }
    };

    const driverLicenceContainer = ref(null);
    const couponStats = ref(null);
    const scVisible = ref(true);
    const couponStatsHeight = ref(0);

    const onScroll = () => {
      if (driverLicenceContainer.value) {
        scVisible.value = driverLicenceContainer.value.$el.getBoundingClientRect().bottom > 0;
      }
    };

    const scrollTo = () => {
      setTimeout(() => {
        const nextCoupon = oneCoupon.value.find((c) => !c.$el.className.includes('processed'));
        if (nextCoupon) {
          couponStatsHeight.value = couponStats.value ? couponStats.value.$el.offsetHeight : 0;
          const offset = !scVisible.value ? couponStatsHeight.value : couponStatsHeight.value * 2;

          const y = nextCoupon.$el.offsetTop - offset;
          window.scroll({ top: y, behavior: 'smooth' });
        }
      }, 3000);
    };

    onMounted(async () => {
      scannerCoupon.setCurrentScanner(routerId.value);
      if (!currentScanner.value.isAppMembre) {
        document.addEventListener('scroll', onScroll, true);
      }
    });

    const fetchCoupons = async (filterTypeIds: number[] | null) => {
      lastTypeFilter.value = filterTypeIds;
      const isAppMembre = currentScanner.value?.isAppMembre;
      if (isAppMembre) {
        const q = {
          searchQuery: searchQuery.value.trim() || undefined,
          page: page.value,
          pageSize: pageSize.value,
        };
        await scannerCoupon.fetchNewCoupons(routerId.value, filterTypeIds, q);
        await scannerCoupon.fetchErroredCoupons(routerId.value, filterTypeIds, {
          searchQuery: q.searchQuery,
          pageSize: 50,
        });
      } else {
        await scannerCoupon.fetchNewCoupons(routerId.value, filterTypeIds);
        await scannerCoupon.fetchErroredCoupons(routerId.value, filterTypeIds);
      }
    };

    const onTypeFilterFetch = async (filterTypeIds: number[] | null) => {
      typeFilterReady.value = true;
      page.value = 1;
      await fetchCoupons(filterTypeIds);
    };

    useDebouncedRef(() => {
      if (!currentScanner.value?.isAppMembre) return;
      page.value = 1;
    }, [searchQuery]);

    useDebouncedRef(async () => {
      if (!currentScanner.value?.isAppMembre || !typeFilterReady.value) return;
      await fetchCoupons(lastTypeFilter.value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchQuery, page, pageSize]);

    onUnmounted(async () => {
      selectedCoupons.value = [];
      coupon.reset();
      driverLicenceData.value = null;
      if (!currentScanner.value?.isAppMembre) {
        document.removeEventListener('scroll', onScroll, true);
      }
    });

    async function applyToAll(
      payload: OdooLookupByPartnerRefResponse & { vehicleId: number },
    ): Promise<void> {
      const odooPartnerId = payload.driverId;
      if (odooPartnerId == null || payload.vehicleId == null) {
        return;
      }
      const { data: appDriver, error, response } = await apiClient.GET('/drivers/by-odoo/{odooId}', {
        params: {
          path: {
            odooId: odooPartnerId,
          },
        },
      });

      let batchPayload: GenerateBatchIdData;

      if (response.ok && appDriver) {
        driverId.value = appDriver.id;
        odooDriverId.value = null;
        fleetVehicleId.value = null;
        batchPayload = { driverId: appDriver.id, vehicleId: payload.vehicleId };
      } else if (response.status === 404) {
        driverId.value = null;
        odooDriverId.value = odooPartnerId;
        fleetVehicleId.value = payload.vehicleId;
        batchPayload = { odooDriverId: odooPartnerId, fleetVehicleId: payload.vehicleId };
      } else {
        console.error(
          error ?? `Unexpected ${response.status} from /drivers/by-odoo/{odooId}; aborting batch.`,
        );
        return;
      }

      const generatedBatchId = await scannerCoupon.generateBatchId(batchPayload, unprocessedCoupons.value);
      if (!generatedBatchId) {
        driverId.value = null;
        odooDriverId.value = null;
        fleetVehicleId.value = null;
        return;
      }
      batchId.value = generatedBatchId;
      await scannerCoupon.fetchBatchCoupons(routerId.value, generatedBatchId);
    }

    const completeImport = async () => {
      openImport.value = true;
    };

    const clearScanner = async () => {
      coupon.reset();
      enableClear.value = false;
      driverLicenceData.value = null;
      batchId.value = '';
      driverLicence.value = '';
      vehicles.value = [];
      vehicleId.value = null;
      driverId.value = null;
      odooDriverId.value = null;
      fleetVehicleId.value = null;
    };

    function getVehicleName(vehicle: OdooVehicles): string {
      return `${vehicle.vignette_id}/${vehicle.name}`;
    }

    return {
      completeImport,
      selectedCoupons,
      print,
      approvedTotal,
      rejectedTotal,
      unprocessedTotal,
      oneCoupon,
      driverLicence,
      vehicle,
      vehicles,
      vehicleId,
      driverId,
      odooDriverId,
      fleetVehicleId,
      count,
      error,
      showReport,
      unprocessedCoupons,
      coupons,
      filteredItems,
      scVisible,
      driverLicenceContainer,
      currentScanner,
      loading,
      driverLicenceData,
      openImport,
      enableClear,
      confirmOpen,
      deleteText,
      batchId,
      couponStats,
      couponStatsHeight,
      searchQuery,
      page,
      pageSize,
      pageSizeOptions,
      pageSizeSelectModel,
      paginationRange,
      listTotalPages,
      onPageSizeChange,
      deleteCallback,
      confirmDeleteAll,
      confirmDeleteOne,
      confirmDeleteSelected,
      clearScanner,
      getVehicleName,
      onceLookUp,
      applyToAll,
      fetchCoupons,
      onTypeFilterFetch,
    };
  },
});
